import { FC, useState, KeyboardEvent } from 'react';
import { AssistantConversationListItem } from '@/types/assistant';

interface Props {
  conversations: AssistantConversationListItem[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onRename: (id: string, title: string) => void;
  onArchive: (id: string) => void;
  loading?: boolean;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
  // Recolher no desktop pra dar espaço ao chat (estado persistido pelo AssistantPage).
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

function formatRelative(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} d`;
  return date.toLocaleDateString('pt-BR');
}

const AssistantConversationSidebar: FC<Props> = ({
  conversations,
  activeId,
  onSelect,
  onNew,
  onRename,
  onArchive,
  loading,
  isMobileOpen = false,
  onMobileClose,
  collapsed = false,
  onToggleCollapse,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  function startEdit(c: AssistantConversationListItem) {
    setEditingId(c.id);
    setDraft(c.title);
  }

  function commitEdit() {
    if (editingId) {
      const title = draft.trim();
      if (title) onRename(editingId, title);
    }
    setEditingId(null);
    setDraft('');
  }

  function handleKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      setEditingId(null);
      setDraft('');
    }
  }

  return (
    <>
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      {/* Rail (desktop, recolhido): só expandir + nova conversa. Devolve ~224px ao chat. */}
      <div
        data-testid="conversation-rail"
        className={`hidden ${collapsed ? 'lg:flex' : 'lg:hidden'} w-14 shrink-0 border-r border-surface-border bg-surface-secondary flex-col items-center gap-2 py-3`}
      >
        <button
          type="button"
          data-testid="expand-conversations"
          onClick={onToggleCollapse}
          aria-label="Expandir conversas"
          title="Expandir conversas"
          className="p-2 rounded-lg hover:bg-surface-hover text-content-tertiary hover:text-content-primary transition"
        >
          <span className="material-symbols-outlined">chevron_right</span>
        </button>
        <button
          type="button"
          onClick={onNew}
          aria-label="Nova conversa"
          title="Nova conversa"
          className="p-2 rounded-lg bg-accent text-surface-primary hover:bg-accent/90 transition"
        >
          <span className="material-symbols-outlined">add</span>
        </button>
      </div>

      <aside
        data-testid="assistant-conversation-sidebar"
        className={`w-[280px] border-r border-surface-border bg-surface-secondary flex flex-col h-full z-50 transition-transform duration-300 fixed left-0 top-0 bottom-0 lg:relative lg:left-auto lg:top-auto lg:bottom-auto lg:translate-x-0 ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${collapsed ? 'lg:hidden' : 'lg:flex'}`}
      >
        <div className="lg:hidden flex justify-end p-2 border-b border-surface-border">
          <button
            type="button"
            onClick={onMobileClose}
            aria-label="Fechar conversas"
            className="p-2 hover:bg-surface-hover rounded-lg"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="p-3 border-b border-surface-border flex items-center gap-2">
          <button
            type="button"
            data-testid="new-conversation"
            onClick={onNew}
            className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-accent text-surface-primary font-semibold py-2.5 hover:bg-accent/90 transition"
          >
            <span className="material-symbols-outlined">add</span>
            Nova conversa
          </button>
          <button
            type="button"
            data-testid="collapse-conversations"
            onClick={onToggleCollapse}
            aria-label="Recolher conversas"
            title="Recolher conversas"
            className="hidden lg:inline-flex p-2 rounded-lg hover:bg-surface-hover text-content-tertiary hover:text-content-primary transition shrink-0"
          >
            <span className="material-symbols-outlined">chevron_left</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {loading && <div className="p-4 text-sm text-content-tertiary">Carregando…</div>}
          {!loading && conversations.length === 0 && (
            <div className="p-4 text-sm text-content-tertiary">Nenhuma conversa ainda.</div>
          )}
          {conversations.map((c) => {
            const active = c.id === activeId;
            const editing = editingId === c.id;
            return (
              <div
                key={c.id}
                data-testid="conversation-item"
                data-active={active}
                className={`group relative rounded-lg mb-1 transition ${
                  active
                    ? 'bg-accent/10 border border-accent/30'
                    : 'hover:bg-surface-border/30 border border-transparent'
                }`}
              >
                {editing ? (
                  <input
                    autoFocus
                    data-testid="rename-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commitEdit}
                    onKeyDown={handleKey}
                    className="w-full bg-surface-primary border border-accent/40 rounded-lg px-3 py-2 text-sm text-content-primary focus:outline-none"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => onSelect(c.id)}
                    className="w-full text-left px-3 py-2.5 pr-14"
                  >
                    <div
                      className={`font-medium text-sm truncate ${
                        active ? 'text-content-primary' : 'text-content-secondary'
                      }`}
                    >
                      {c.title}
                    </div>
                    <div className="text-2xs text-content-tertiary mt-0.5">
                      {formatRelative(c.last_message_at)}
                    </div>
                  </button>
                )}

                {!editing && (
                  <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
                    <button
                      type="button"
                      data-testid="rename-conversation"
                      onClick={(e) => {
                        e.stopPropagation();
                        startEdit(c);
                      }}
                      aria-label="Renomear conversa"
                      className="p-1 rounded hover:bg-surface-hover text-content-tertiary hover:text-content-primary"
                    >
                      <span className="material-symbols-outlined text-base leading-none">edit</span>
                    </button>
                    <button
                      type="button"
                      data-testid="archive-conversation"
                      onClick={(e) => {
                        e.stopPropagation();
                        onArchive(c.id);
                      }}
                      aria-label="Arquivar conversa"
                      className="p-1 rounded hover:bg-surface-hover text-content-tertiary hover:text-red-400"
                    >
                      <span className="material-symbols-outlined text-base leading-none">
                        archive
                      </span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </aside>
    </>
  );
};

export default AssistantConversationSidebar;
